import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/lib/ThemeContext";
import LegalFooter from "@/components/LegalFooter";

// Bundle F-5: public status page fed by GET /api/health/deep (self-reported). STATUS_PAGE_URL is read with an
// empty default; the independent-history link only renders once Liam sets it at build time.
const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_PAGE_URL = process.env.REACT_APP_STATUS_PAGE_URL || "";
const REFRESH_MS = 60_000;
const COLOR = { ok: "#22c55e", warn: "#f59e0b", bad: "#ef4444", unknown: "#9ca3af", info: "#9ca3af" };

function rows(data) {
  if (!data) return [];
  const c = data.checks;
  const age = (s) => (s == null ? "no run recorded" : s < 120 ? `last run ${s}s ago` : s < 7200 ? `last run ${Math.round(s / 60)} min ago` : `last run ${Math.round(s / 3600)} h ago`);
  const analyticsOk = c.analytics_configured || Boolean(process.env.REACT_APP_POSTHOG_KEY); // the key lives in the frontend build
  return [
    { id: "database", label: "Database", level: c.database.ok ? "ok" : "bad", text: c.database.ok ? "Responding normally." : "Not responding — the app cannot save or load data.", detail: c.database.latency_ms != null ? `${c.database.latency_ms} ms` : "" },
    { id: "scheduler_reminders", label: "Reminder scheduler", level: c.scheduler_reminders.ok ? "ok" : "warn", text: c.scheduler_reminders.ok ? "Running on schedule." : "Has not run recently — reminders may be late.", detail: age(c.scheduler_reminders.last_tick_age_s) },
    { id: "scheduler_purge", label: "Deletion archive clean-up", level: c.scheduler_purge.ok ? "ok" : "warn", text: c.scheduler_purge.ok ? "Running on schedule (daily)." : "Has not run recently — expired archives are kept a little longer.", detail: age(c.scheduler_purge.last_tick_age_s) },
    { id: "email", label: "Transactional e-mail", level: c.email_configured ? "ok" : "warn", text: c.email_configured ? "Configured." : "Not configured — sign-up and reset e-mails cannot be sent.", detail: "" },
    { id: "analytics", label: "Product analytics", level: "info", text: analyticsOk ? "Configured." : "Not configured.", detail: "informational" },
    { id: "errors", label: "Error monitoring", level: "info", text: c.error_tracking_configured ? "Configured." : "Not configured.", detail: "informational" },
  ];
}

export default function StatusPage() {
  const { tokens: t } = useTheme();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [at, setAt] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/health/deep`, { cache: "no-store" });
      const body = await r.json();
      setData(body); setError(""); setAt(new Date());
    } catch { setError("The status check itself could not be reached."); setAt(new Date()); }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, REFRESH_MS); return () => clearInterval(id); }, [load]);
  useEffect(() => { document.title = "Status • MMNTM"; return () => { document.title = "MMNTM"; }; }, []);

  const list = rows(data);
  // Overall is derived only from the rows that affect status (database, schedulers, e-mail); "info" rows never count.
  const overall = error ? "bad" : !data ? "unknown" : list.some((r) => r.level === "bad") ? "bad" : list.some((r) => r.level === "warn") ? "warn" : "ok";
  const headline = { ok: "All systems normal", warn: "Degraded — some parts need attention", bad: "Major outage", unknown: "Checking…" }[overall];
  const body = { color: "var(--tm-text-secondary)", lineHeight: 1.7 };
  const link = { color: "var(--tm-accent)", textDecoration: "underline" };
  return (
    <div data-testid="status-page" style={{ background: "var(--tm-bg-primary, " + t.bgPrimary + ")", minHeight: "calc(100vh - var(--topbar-height, 0px))" }}>
      <article className="mx-auto px-6 sm:px-8 py-10 sm:py-12" style={{ maxWidth: "800px" }}>
        <h1 className="font-bold mb-2" style={{ color: "var(--tm-text-primary)", fontSize: "36px", letterSpacing: "-0.02em", lineHeight: 1.1 }}>MMNTM status</h1>
        <div className="flex items-center gap-3 mb-8 pb-6 text-sm" style={{ ...body, borderBottom: `1px solid var(--tm-border-color, ${t.borderColor})` }}>
          <span className="inline-block h-3 w-3 rounded-full shrink-0" style={{ background: COLOR[overall] }} aria-hidden />
          <span className="font-semibold" style={{ color: "var(--tm-text-primary)" }} data-testid="status-headline">{headline}</span>
          {at && <span className="ml-auto text-xs" data-testid="status-checked-at">checked {at.toLocaleTimeString()} · refreshes every minute</span>}
        </div>
        {error && <p className="text-sm mb-6" style={{ color: COLOR.bad }} data-testid="status-error">{error}</p>}
        <ul className="space-y-3 mb-10" data-testid="status-rows">
          {list.map((r) => (
            <li key={r.id} className="flex items-start gap-3 text-sm" data-testid={`status-row-${r.id}`}>
              <span className="mt-1.5 inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ background: COLOR[r.level] }} aria-label={r.level} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2"><span className="font-semibold" style={{ color: "var(--tm-text-primary)" }}>{r.label}</span><span className="text-xs" style={body}>{r.detail}</span></div>
                <div style={body}>{r.text}</div>
              </div>
            </li>
          ))}
        </ul>
        <div className="text-sm space-y-2" style={body} data-testid="status-footnote">
          <p>Self-reported by the application.{STATUS_PAGE_URL ? <> Independent uptime history: <a href={STATUS_PAGE_URL} style={link} rel="noreferrer" target="_blank" data-testid="status-external-link">{STATUS_PAGE_URL}</a>.</> : null}</p>
          {data?.version && <p className="text-xs">Build {data.version}</p>}
        </div>
        <div className="mt-10 pt-6 text-sm" style={{ borderTop: `1px solid var(--tm-border-color, ${t.borderColor})`, color: "var(--tm-text-secondary)" }}>
          See also <Link to="/security" style={link}>Security</Link>, <Link to="/privacy" style={link}>Privacy Policy</Link> and <Link to="/terms" style={link}>Terms of Service</Link>.
        </div>
      </article>
      <LegalFooter />
    </div>
  );
}
